import { Component, h, Event, type EventEmitter, Prop } from '@stencil/core';
import { makeLogger } from '../../../logger/logger';
import { SearchTableTheme } from '../search-tab/tabular/SearchTableConfiguration';
import type { labelProviderInterface } from '../../LabelProviderInterface';
import {location} from '../../../constants/pageLocation';
import { SupportChatWidget } from '../search-tab/subcomponents/dashboard-tab-view/dashboard-widgets/support-chat-widget';

const { warn } = makeLogger({
  pathToCurrentFile: 'components/result-page/no-results/uiaas-search-no-results.tsx',
  loggersList: ['warn']
});

@Component({
  tag: 'uiaas-search-no-results',
  shadow: true,
  styleUrl: 'uiaas-search-no-results.scss',
})
export class SearchNoResults {
    /**
     * Provides a string with query value for the component
     */
    @Prop() readonly query: string;

    /**
     * Enables event on zero result
     *! Remove after global decommissioning
     */
    @Prop() readonly globalDecommissioning: boolean = false;
    /*
     * List of locations where the searched product is available
     */
    @Prop() readonly productNaCountrySelectorLocales: string = '';
    @Prop() readonly isProductNaCountrySelector: boolean = false;
    /*
    * Total number of all available documents
    */
    @Prop() readonly totalHits: number;
    @Prop() readonly tabName: string;
    @Prop() readonly analyticsTabName?: string;
    @Prop() readonly rtl: boolean = false;
    @Prop() readonly isSupport: boolean;

    /**
     * label provider
     */
    @Prop({ mutable: true })
      labelProvider?: labelProviderInterface;

    /**
    * Provides a theme to render no results message
    */
    @Prop() readonly theme?: SearchTableTheme;
    @Prop() readonly language?: string;
    @Prop() readonly country?: string;
    @Prop() readonly brand?: string;

    /**
    * Controlling country selector on SRP
    */
    @Event({
      eventName: 'search:isZeroResultPage',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      isZeroResultPage: EventEmitter;

    componentWillLoad() {
        if (!this.labelProvider) {
          this.labelProvider = (name, fallback) => {
              warn(
                'Label provider not defined for the component ',
                this,
                [name, fallback]
              );
              return fallback;
          };
        }
    }

    render() {
        switch (this.theme) {
        case SearchTableTheme.Basic:
        case SearchTableTheme.Cybersecurity:
          return this.basicMessage();
        case SearchTableTheme.Quotes:
          return this.quoteMessage();
        default:
          return this.basicMessage();
        }
    }

    private basicMessage() {
        if (this.brand === 'myschneider') {
          return this.basicMessageMySchneider();
        }
        return this.basicMessageDaflault();
    }

    private basicMessageMySchneider() {
        return (
          <section>
            <div class='no-result__container'>
              <h4 class="no-result__title">
                {this.labelProvider(
                  'no-result',
                  'No result in the current tab for:'
                )}
              </h4>
              <h3 class="no-result__query">"{this.query}"</h3>
              <h4 class="no-result__message">
                {this.labelProvider(
                  'refine-your-query',
                  'Please refine your query'
                )}
              </h4>
              {this.productCountrySelector()}
              {this.smartCountrySelector()}
            </div>
          </section>
        );
    }

    private getTitle () {
        const translation = this.labelProvider(`${this.tabName}.no-results-for`, 'We could not find any results for');
        if(translation?.includes('{QUERY}')) {
          const splittedTranslation = translation.split('{QUERY}');
          return (
            <span>
              {splittedTranslation[0]}
              <span class='no-results__query'>{`${this.query}`}</span>
              {splittedTranslation[1]}
            </span>
          );
        } else {
          return [
            <span>{this.labelProvider(
              `${this.tabName}.no-results-for`,
              'We could not find any results for'
            )}</span>,
            <span class='no-results__query'>{` ${this.query}`}</span>
          ];
        }
    }

    private basicMessageDaflault() {
        const title = this.getTitle();
        const verifyReferenceText = this.labelProvider(`${this.tabName}.no-results-verify-references`, 'Verify if all commercial references are spelled correctly (eg; ATV320U07M2C).');
        return (
          <section>
            <div class='no-results__container'>
              <div class="no-results__title">
                {title}
              </div>
              <div class='no-results__message'>
                <p>{this.labelProvider('no-results-tips', 'Here are some tips that may help you:')}</p>
                <ul>
                  <li>{this.labelProvider('no-results-double-check', 'Double-check if your search query is spelled correctly.')}</li>
                  {verifyReferenceText !== '-' && <li>{verifyReferenceText}</li>}
                  <li>{this.labelProvider('no-results-use-synonyms', 'Try using synonyms for your original terms.')}</li>
                  <li>{this.labelProvider('no-results-suggestions', 'Try one of the suggestions that are displayed as you type in the main search input.')}</li>
                </ul>
              </div>
              {this.productCountrySelector()}
              {this.smartCountrySelector()}
              {this.supportChatWidget()}
            </div>
          </section>
        );
    }

    private quoteMessage() {
        return (
          <div>
            <span class='no-result-quotes'>
              {this.labelProvider('searchtab.noresults', 'No results with current criteria. Please check your spelling and refine the query or start fresh by clicking on "reset all".')}
            </span>
            {this.smartCountrySelector()}
          </div>
        );
    }

    // The following method load country selector
    //! To remove after global decommissioning
    private smartCountrySelector() {
        return(
          this.globalDecommissioning ? <uiaas-smart-country-selector-banner language={this.language} labelProvider={this.labelProvider} /> : ''
        );
    }

    private productCountrySelector() {
        return(
          this.isProductNaCountrySelector && this.productNaCountrySelectorLocales && <div class="no-results__product-na-banner">
            <uiaas-product-na-country-selector-banner
              country={this.country}
              language={this.language}
              labelProvider={this.labelProvider}
              totalHits={this.totalHits}
              isHeaderVisible={false}
              productNaLocales={this.productNaCountrySelectorLocales}
              tabName={this.analyticsTabName || this.tabName}
              location={location.NO_RESULTS}
              rtl={this.rtl}
            />
          </div>);
    };

    private supportChatWidget() {
        return this.tabName?.includes('all') && this.isSupport ? <SupportChatWidget/> : '';
    }
}
